import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import Utils from 'models/helper/Utils';
import RSVP from 'rsvp';
import SequencerRendererHelper from 'models/audioEditor/SequencerRendererHelper';
import SequencerRendererUtils from 'models/audioEditor/SequencerRendererUtils';
import SequencerTimelineExportHelper from 'models/audioEditor/SequencerTimelineExportHelper';
import PersistentModels from 'services/PersistentModels';
import TrebbleClientAPIHelper from 'models/helper/TrebbleClientAPI';
import FileUploadHelper from 'models/helper/FileUploadHelper';
import FeatureRolloutHelper from 'models/helper/FeatureRolloutHelper';
import JSZip from 'jszip';
import { Document, Paragraph, TextRun, Packer } from 'docx';
import { saveAs } from 'file-saver';
import ti18n from 'i18n!nls/ExportPopupi18n';
import sequencerI18n from 'i18n!nls/Sequenceri18n';

// Import new modular exporters
import EdlExporter from 'models/audioEditor/export/formatExporters/EdlExporter';
import FcpxmlExporter from 'models/audioEditor/export/formatExporters/FcpxmlExporter';
import PremiereXmlExporter from 'models/audioEditor/export/formatExporters/PremiereXmlExporter';
import SesxExporter from 'models/audioEditor/export/formatExporters/SesxExporter';
import BaseExporter from 'models/audioEditor/export/formatExporters/BaseExporter';
import TimecodeConverter from 'models/audioEditor/export/utils/TimecodeConverter';

/**
 * SequencerExportHelper - Handles export of Trebble projects to EDL and FCPXML formats
 * 
 * This helper leverages the existing rendering pipeline to extract precise timing data
 * from the sequencer timeline and converts it to professional editing formats.
 */
const SequencerExportHelper = Backbone.Model.extend({
    idAttribute: 'id',


    /**
     * Default export options
     */
    getDefaultOptions: function() {
        return {
            includeDeletedNodes: false,          // Include soft-deleted content
            frameRate: 25,                       // Target frame rate
            includeWordTimestamps: false,        // Generate subtitle tracks
            mediaPathType: 'url',               // "url" | "blob" | "relative"
            reelNamePrefix: 'V',                // Prefix for reel names
            startTimecode: '00:00:00:00',       // Starting timecode offset
            projectTitle: 'Trebble Project',    // Default project title
            includeMedia: false,                // Whether to include media files in zip package
            edlFormat: 'cmx3600',               // 'cmx3600' or 'samplitude'
            sampleRate: 48000,                  // For Samplitude EDL
            outputChannels: 2,                  // For Samplitude EDL
            useEnhancedAudio: false,            // Disable enhanced audio until files are generated
        };
    },

    /**
     * Extract timeline data from a sequencer using the existing rendering pipeline
     * This reuses the same data extraction logic as the cloud video renderer
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<Array>} Array of timeline clip objects
     */
    extractTimelineData: async function(sequencer, startNode, endNode, options) {
        options = _.extend(this.getDefaultOptions(), options || {});

        try {
            // Use SequencerRendererHelper to access the renderer without using private methods
            // This follows the established pattern throughout the codebase
            const rendererHelper = SequencerRendererHelper.getInstance();
            
            // First ensure the sequencer is rendered using the public helper method
            await rendererHelper._renderSequencer(
                sequencer,
                false, // applyMasterProcessing
                null, // customRenderFunctionToExecute
                options.includeDeletedNodes ? null : SequencerTimelineExportHelper.getInstance()._createNonDeletedNodeValidator(),
                options.includeDeletedNodes
            );

            // Now use the new public method to extract timeline data
            // This delegates to SequencerTimelineExportHelper internally
            return await rendererHelper.extractTimelineDataForExport(
                sequencer,
                startNode,
                endNode,
                options
            );

        } catch (error) {
            console.error('Error extracting timeline data:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export timeline data as EDL format
     * 
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} EDL content as text
     */
    exportEDL: function(timelineData, options) {
        options = _.extend(this.getDefaultOptions(), options || {});

        // Check if Samplitude or Reaper format is requested
        if (options.edlFormat === 'samplitude') {
            return this.exportSamplitudeEDL(timelineData, options);
        } else if (options.edlFormat === 'reaper') {
            // Create EdlExporter instance for Reaper EDL
            const edlExporter = new EdlExporter();
            return edlExporter.exportReaperEDL(timelineData, options);
        }

        try {
            // Create EdlExporter instance for utility methods
            const edlExporter = new EdlExporter();
            // Generate proper CMX 3600 EDL header
            let edlContent = `TITLE: ${options.projectTitle}\n`;
            
            // Set FCM (Frame Count Mode) based on frame rate
            if (options.frameRate === 29.97 || options.frameRate === 59.94) {
                edlContent += `FCM: DROP FRAME\n`;
            } else {
                edlContent += `FCM: NON-DROP FRAME\n`;
            }
            
            edlContent += '\n';

            // Create track assignment map - each unique source file gets its own track
            const uniqueSources = new Set();
            timelineData.forEach(clip => {
                if (clip.fileName) {
                    uniqueSources.add(clip.fileName);
                }
            });
            
            const sourceToTrackMap = new Map();
            let trackCounter = 1;
            for (const fileName of uniqueSources) {
                sourceToTrackMap.set(fileName, trackCounter);
                trackCounter++;
            }

            // Generate EDL events
            timelineData.forEach((clip, index) => {
                const eventNumber = (index + 1).toString().padStart(3, '0');
                const reelName = (clip.reelName || options.reelNamePrefix).padEnd(8).substring(0, 8);
                const trackType = clip.hasVideo ? 'V' : 'A';
                
                // Assign track number based on source file
                const trackNumber = sourceToTrackMap.get(clip.fileName) || 1;
                const track = trackType + trackNumber.toString();
                const transitionType = 'C'; // Cut transition
                
                // Convert seconds to timecode using new modular converter
                const recordIn = TimecodeConverter.convertSecondsToTimecode(clip.startTimeInSeconds, options.frameRate);
                const recordOut = TimecodeConverter.convertSecondsToTimecode(clip.endTimeInSeconds, options.frameRate);
                const sourceIn = clip.sourceOffsetTimecode || TimecodeConverter.convertSecondsToTimecode(clip.sourceStartInSeconds, options.frameRate);
                const sourceOut = TimecodeConverter.convertSecondsToTimecode(clip.sourceEndInSeconds, options.frameRate);

                // CMX 3600 EDL format: EVENT  REEL     TRACK  TRANS   SOURCE_IN    SOURCE_OUT   RECORD_IN    RECORD_OUT
                edlContent += `${eventNumber}  ${reelName} ${track.padEnd(6)} ${transitionType}        ${sourceIn} ${sourceOut} ${recordIn} ${recordOut}\n`;
                
                // Add source file comment with appropriate path (optional but helpful)
                if (clip.fileName) {
                    const sourceFilePath = edlExporter.getMediaFilePath(clip, options);
                    edlContent += `* FROM CLIP NAME: ${clip.fileName}\n`;
                    edlContent += `* SOURCE FILE: ${sourceFilePath}\n`;
                    edlContent += `* TRACK: ${trackType}${trackNumber} (${clip.fileName})\n`;
                }
                
                edlContent += '\n';
            });

            return edlContent;

        } catch (error) {
            console.error('Error generating EDL:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export timeline data as Samplitude EDL format (compatible with Reaper)
     * 
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} Samplitude EDL content as text
     */
    exportSamplitudeEDL: function(timelineData, options) {
        options = _.extend(this.getDefaultOptions(), options || {});

        try {
            // Create EdlExporter instance for utility methods
            const edlExporter = new EdlExporter();
            // Default sample rate (can be overridden in options)
            const sampleRate = options.sampleRate || 48000;
            const channels = options.outputChannels || 2;
            
            // Generate Samplitude EDL header
            let edlContent = `Samplitude EDL File Format Version 1.5\n`;
            edlContent += `Title: "${options.projectTitle}"\n`;
            edlContent += `Sample Rate: ${sampleRate}\n`;
            edlContent += `Output Channels: ${channels}\n`;
            edlContent += `\n\n`;

            // Create source table - list all unique source files
            const uniqueSources = new Map();
            let sourceIndex = 1;
            
            timelineData.forEach(clip => {
                if (clip.fileName && !uniqueSources.has(clip.fileName)) {
                    uniqueSources.set(clip.fileName, sourceIndex);
                    sourceIndex++;
                }
            });

            // Write source table
            edlContent += `Source Table Entries: ${uniqueSources.size}\n`;
            for (const [fileName, index] of uniqueSources) {
                const sourceFilePath = edlExporter.getMediaFilePathForClip(fileName, timelineData, options);
                edlContent += `   ${index} "${sourceFilePath}"\n`;
            }
            edlContent += `\n`;

            // Group clips by source file - each unique source gets its own track
            const tracksBySource = new Map();
            let currentTrackNumber = 1;
            
            // Assign track numbers to each unique source file
            for (const [fileName, sourceId] of uniqueSources) {
                tracksBySource.set(fileName, currentTrackNumber);
                currentTrackNumber++;
            }
            
            // Generate track headers and entries for each source file
            for (const [fileName, trackNumber] of tracksBySource) {
                const trackName = fileName.replace(/\.[^/.]+$/, ""); // Remove file extension for track name
                edlContent += `Track ${trackNumber}: "${trackName}" Solo: 0 Mute: 0\n`;
                
                // Find all clips from this source file
                const clipsFromThisSource = timelineData.filter(clip => clip.fileName === fileName);
                
                // Generate track entries for clips from this source
                clipsFromThisSource.forEach(clip => {
                    const sourceId = uniqueSources.get(clip.fileName) || 1;
                    
                    // Convert times to samples
                    const playIn = Math.round(clip.startTimeInSeconds * sampleRate);
                    const playOut = Math.round(clip.endTimeInSeconds * sampleRate);
                    const recordIn = Math.round(clip.sourceStartInSeconds * sampleRate);
                    const recordOut = Math.round(clip.sourceEndInSeconds * sampleRate);
                    
                    // Volume in dB (default 0.0)
                    const volume = clip.volume !== undefined ? edlExporter.convertLinearToDb(clip.volume) : 0.0;
                    
                    // Mute and lock flags
                    const mute = clip.muted ? '1' : '0';
                    const lock = clip.locked ? '1' : '0';
                    
                    // Fade settings (defaults)
                    const fadeIn = 0;
                    const fadeInPercent = 0;
                    const fadeInCurve = '"*default"';
                    const fadeOut = 0;
                    const fadeOutPercent = 0;
                    const fadeOutCurve = '"*default"';
                    
                    // Clip name
                    const clipName = clip.clipName || clip.fileName || 'clip';
                    
                    // Samplitude EDL line format:
                    // Source Track Play-In Play-Out Record-In Record-Out Vol(dB) MT LK FadeIn % CurveType FadeOut % CurveType Name
                    edlContent += `${sourceId}  ${trackNumber}  ${playIn}  ${playOut}  ${recordIn}  ${recordOut}  ${volume.toFixed(1)}  ${mute}  ${lock}  ${fadeIn}  ${fadeInPercent}  ${fadeInCurve}  ${fadeOut}  ${fadeOutPercent}  ${fadeOutCurve}  "${clipName}"\n`;
                });
                
                edlContent += `\n`;
            }

            // Add track volume and pan settings for each track
            for (const [fileName, trackNumber] of tracksBySource) {
                edlContent += `Volume for Track ${trackNumber}:\n`;
                edlContent += `   0 0.000\n`;
                edlContent += `\n`;
                edlContent += `Pan for Track ${trackNumber}:\n`;
                edlContent += `   0 1.00000\n`;
                edlContent += `\n`;
            }

            return edlContent;

        } catch (error) {
            console.error('Error generating Samplitude EDL:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export timeline data as FCPXML format
     * 
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options  
     * @returns {String} FCPXML content as XML
     */
    exportFCPXML: function(timelineData, options) {
        options = _.extend(this.getDefaultOptions(), options || {});

        try {
            // Create FcpxmlExporter instance and delegate to it
            const fcpxmlExporter = new FcpxmlExporter();
            return fcpxmlExporter.export(timelineData, options);

        } catch (error) {
            console.error('Error generating FCPXML:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export timeline data as Adobe Premiere XML format
     *
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} Adobe Premiere XML content as XML
     */
    exportPremiereXML: function(timelineData, options) {
        options = _.extend(this.getDefaultOptions(), options || {});

        try {
            // Create PremiereXmlExporter instance and delegate to it
            const premiereExporter = new PremiereXmlExporter();
            return premiereExporter.export(timelineData, options);

        } catch (error) {
            console.error('Error generating Adobe Premiere XML:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export timeline data as Adobe Audition SESX format
     *
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} SESX content as XML
     */
    exportSESX: function(timelineData, options) {
        options = _.extend(this.getDefaultOptions(), options || {});

        try {
            // Create SesxExporter instance and delegate to it
            const sesxExporter = new SesxExporter();
            return sesxExporter.export(timelineData, options);

        } catch (error) {
            console.error('Error generating SESX:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },



    /**
     * Convert seconds to FCPXML duration format (e.g., "100/25s" for 4 seconds at 25fps)
     */
    _convertSecondsToFCPXMLDuration: function(seconds, frameRate) {
        frameRate = frameRate || 25;
        const totalFrames = Math.round(seconds * frameRate);
        return `${totalFrames}/${frameRate}s`;
    },

    /**
     * Calculate total duration of timeline data
     */
    _getTotalDuration: function(timelineData) {
        if (!timelineData || timelineData.length === 0) {
            return 0;
        }
        
        // Find the maximum end time
        let maxEndTime = 0;
        timelineData.forEach(clip => {
            if (clip.endTimeInSeconds > maxEndTime) {
                maxEndTime = clip.endTimeInSeconds;
            }
        });
        
        return maxEndTime;
    },


    /**
     * Get node type string
     */
    _getNodeType: function(sequencerNode) {
        const utils = Utils.getInstance();
        
        if (utils.isWordSequencerNodeInstance(sequencerNode)) {
            return 'word';
        } else if (utils.isAudioSequencerNodeInstance(sequencerNode)) {
            return 'audio';
        } else if (utils.isVideoSequencerNodeInstance(sequencerNode)) {
            return 'video';
        } else if (utils.isPauseSequencerNodeInstance(sequencerNode)) {
            return 'pause';
        } else if (utils.isDeletedSequencerNodeInstance(sequencerNode)) {
            return 'deleted';
        }
        
        return 'unknown';
    },

    /**
     * Extract word timestamp information
     */
    _extractWordTimestamps: function(nodeInfo) {
        const sequencerNode = nodeInfo.getSequencerNodeReference();
        const utils = Utils.getInstance();
        
        if (!utils.isWordSequencerNodeInstance(sequencerNode)) {
            return null;
        }

        try {
            return {
                word: sequencerNode.getWordText ? sequencerNode.getWordText() : '',
                startTime: nodeInfo.getStartTimeOffset() / 1000,
                endTime: nodeInfo.getEndTimeOffset() / 1000,
                speaker: sequencerNode.getSpeakerId ? sequencerNode.getSpeakerId() : null,
                confidence: sequencerNode.getConfidenceScore ? sequencerNode.getConfidenceScore() : null
            };
        } catch (error) {
            console.warn('Error extracting word timestamps:', error);
            return null;
        }
    },

    /**
     * Get the appropriate file path for media based on export options
     */
    _getMediaFilePath: function(clip, options) {
        if (options.mediaPathType === 'relative') {
            return `media/${clip.fileName}`;
        } else if (options.mediaPathType === 'blob') {
            return clip.sourceMediaUrl;
        } else {
            // Default to URL
            return clip.sourceMediaUrl || clip.fileName;
        }
    },

    /**
     * Get media file path for a specific clip by filename
     */
    _getMediaFilePathForClip: function(fileName, timelineData, options) {
        // Find the first clip with this filename to get the source URL
        const clip = timelineData.find(c => c.fileName === fileName);
        if (clip) {
            const baseExporter = new BaseExporter();
            return baseExporter.getMediaFilePath(clip, options);
        }
        return fileName; // Fallback to just the filename
    },

    /**
     * Convert linear volume (0.0-1.0) to decibels
     */
    _convertLinearToDb: function(linearVolume) {
        if (linearVolume <= 0) {
            return -150; // Minimum dB value
        }
        return 20 * Math.log10(linearVolume);
    },


    /**
     * Export project as EDL format
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @param {Boolean} options.includeMedia - If true, creates zip with media files; if false, downloads just EDL file
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<void>} Downloads the file or zip package
     */
    exportEDLProject: async function(sequencer, startNode, endNode, options, progressCallback) {
        try {
            options = _.extend(this.getDefaultOptions(), options || {});
            
            // Set appropriate media path type based on includeMedia option
            if (options.includeMedia) {
                options.mediaPathType = 'relative'; // Use relative paths for zip package
            }
            
            // Report initial progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXTRACTING_TIMELINE_DATA'));
            }
            
            // Extract timeline data
            const timelineData = await this.extractTimelineData(sequencer, startNode, endNode, options);
            
            // Report progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'GENERATING_EDL_CONTENT'));
            }
            
            // Generate EDL content
            const edlContent = this.exportEDL(timelineData, options);
            
            const projectName = options.projectTitle.replace(/[^a-zA-Z0-9]/g, '_');
            
            if (options.includeMedia) {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'CREATING_MEDIA_PACKAGE'));
                }
                
                // Create zip package with media files
                const zip = new JSZip();
                
                // Add EDL file
                zip.file(`${projectName}.edl`, edlContent);
                
                // Add source media files
                await this._addMediaFilesToZip(zip, timelineData, 'media/', sequencer);
                
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }
                
                // Generate and download zip
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                this.downloadFile(zipBlob, `${projectName}_EDL_Package.zip`, 'application/zip');
            } else {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }
                
                // Download just the EDL file
                this.downloadFile(edlContent, `${projectName}.edl`, 'text/plain');
            }
            
            // Report completion
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXPORT_COMPLETE_MESSAGE'));
            }
            
        } catch (error) {
            console.error('Error exporting EDL project:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export project as FCPXML format
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @param {Boolean} options.includeMedia - If true, creates zip with media files; if false, downloads just FCPXML file
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<void>} Downloads the file or zip package
     */
    exportFCPXMLProject: async function(sequencer, startNode, endNode, options, progressCallback) {
        try {
            options = _.extend(this.getDefaultOptions(), options || {});
            
            // Set appropriate media path type based on includeMedia option
            if (options.includeMedia) {
                options.mediaPathType = 'relative'; // Use relative paths for zip package
            }
            
            // Report initial progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXTRACTING_TIMELINE_DATA'));
            }
            
            // Extract timeline data
            const timelineData = await this.extractTimelineData(sequencer, startNode, endNode, options);
            
            // Report progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'GENERATING_FCPXML_CONTENT'));
            }
            
            // Generate FCPXML content
            const fcpxmlContent = this.exportFCPXML(timelineData, options);
            
            const projectName = options.projectTitle.replace(/[^a-zA-Z0-9]/g, '_');
            
            if (options.includeMedia) {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'CREATING_MEDIA_PACKAGE'));
                }
                
                // Create zip package with media files
                const zip = new JSZip();
                
                // Add FCPXML file
                zip.file(`${projectName}.fcpxml`, fcpxmlContent);
                
                // Add source media files
                await this._addMediaFilesToZip(zip, timelineData, 'media/', sequencer);
                
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }
                
                // Generate and download zip
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                this.downloadFile(zipBlob, `${projectName}_FCPXML_Package.zip`, 'application/zip');
            } else {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }
                
                // Download just the FCPXML file
                this.downloadFile(fcpxmlContent, `${projectName}.fcpxml`, 'application/xml');
            }
            
            // Report completion
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXPORT_COMPLETE_MESSAGE'));
            }
            
        } catch (error) {
            console.error('Error exporting FCPXML project:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Convenience method: Export CMX 3600 EDL file only (without media)
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the EDL file
     */
    exportEDLFile: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: false, edlFormat: 'cmx3600' });
        return this.exportEDLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export CMX 3600 EDL with media files in zip package
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the zip package
     */
    exportEDLWithMedia: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: true, edlFormat: 'cmx3600' });
        return this.exportEDLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export Samplitude EDL file only (without media)
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the EDL file
     */
    exportSamplitudeEDLFile: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: false, edlFormat: 'samplitude' });
        return this.exportEDLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export Samplitude EDL with media files in zip package
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the zip package
     */
    exportSamplitudeEDLWithMedia: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: true, edlFormat: 'samplitude' });
        return this.exportEDLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export Reaper EDL file only (without media)
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the EDL file
     */
    exportReaperEDLFile: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: false, edlFormat: 'reaper' });
        return this.exportEDLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export Reaper EDL with media files in zip package
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the zip package
     */
    exportReaperEDLWithMedia: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: true, edlFormat: 'reaper' });
        return this.exportEDLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export FCPXML file only (without media)
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the FCPXML file
     */
    exportFCPXMLFile: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: false });
        return this.exportFCPXMLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export FCPXML with media files in zip package
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the zip package
     */
    exportFCPXMLWithMedia: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: true });
        return this.exportFCPXMLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Export project as Adobe Premiere XML format
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @param {Boolean} options.includeMedia - If true, creates zip with media files; if false, downloads just XML file
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<void>} Downloads the file or zip package
     */
    exportPremiereXMLProject: async function(sequencer, startNode, endNode, options, progressCallback) {
        try {
            options = _.extend(this.getDefaultOptions(), options || {});
            
            // Set appropriate media path type based on includeMedia option
            if (options.includeMedia) {
                options.mediaPathType = 'relative'; // Use relative paths for zip package
            }
            
            // Report initial progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXTRACTING_TIMELINE_DATA'));
            }
            
            // Extract timeline data
            const timelineData = await this.extractTimelineData(sequencer, startNode, endNode, options);
            
            // Report progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'GENERATING_PREMIERE_XML_CONTENT'));
            }
            
            // Generate Adobe Premiere XML content
            const xmlContent = this.exportPremiereXML(timelineData, options);
            
            const projectName = options.projectTitle.replace(/[^a-zA-Z0-9]/g, '_');
            
            if (options.includeMedia) {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'CREATING_MEDIA_PACKAGE'));
                }
                
                // Create zip package with media files
                const zip = new JSZip();
                
                // Add XML file
                zip.file(`${projectName}.xml`, xmlContent);
                
                // Add source media files
                await this._addMediaFilesToZip(zip, timelineData, 'media/', sequencer);
                
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }
                
                // Generate and download zip
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                this.downloadFile(zipBlob, `${projectName}_Premiere_Package.zip`, 'application/zip');
            } else {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }
                
                // Download just the XML file
                this.downloadFile(xmlContent, `${projectName}.xml`, 'application/xml');
            }
            
            // Report completion
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXPORT_COMPLETE_MESSAGE'));
            }
            
        } catch (error) {
            console.error('Error exporting Adobe Premiere XML project:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },


    /**
     * Convenience method: Export Adobe Premiere XML file only (without media)
     * 
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the XML file
     */
    exportPremiereXMLFile: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: false });
        return this.exportPremiereXMLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export Adobe Premiere XML with media files in zip package
     *
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the zip package
     */
    exportPremiereXMLWithMedia: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: true });
        return this.exportPremiereXMLProject(sequencer, startNode, endNode, options);
    },

    /**
     * Export project as Adobe Audition SESX format
     *
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @param {Boolean} options.includeMedia - If true, creates zip with media files; if false, downloads just SESX file
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<void>} Downloads the file or zip package
     */
    exportSESXProject: async function(sequencer, startNode, endNode, options, progressCallback) {
        try {
            options = _.extend(this.getDefaultOptions(), options || {});

            // Set appropriate media path type based on includeMedia option
            if (options.includeMedia) {
                options.mediaPathType = 'relative'; // Use relative paths for zip package
            }

            // Report initial progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXTRACTING_TIMELINE_DATA'));
            }

            // Extract timeline data
            const timelineData = await this.extractTimelineData(sequencer, startNode, endNode, options);

            // Report progress
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'GENERATING_SESX_CONTENT'));
            }

            // Generate SESX content
            const sesxContent = this.exportSESX(timelineData, options);

            const projectName = options.projectTitle.replace(/[^a-zA-Z0-9]/g, '_');

            if (options.includeMedia) {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'CREATING_MEDIA_PACKAGE'));
                }

                // Create zip package with media files
                const zip = new JSZip();

                // Add SESX file
                zip.file(`${projectName}.sesx`, sesxContent);

                // Add source media files
                await this._addMediaFilesToZip(zip, timelineData, 'media/', sequencer);

                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }

                // Generate and download zip
                const zipBlob = await zip.generateAsync({ type: 'blob' });
                this.downloadFile(zipBlob, `${projectName}_SESX_Package.zip`, 'application/zip');
            } else {
                // Report progress
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'FINALIZING_DOWNLOAD'));
                }

                // Download just the SESX file
                this.downloadFile(sesxContent, `${projectName}.sesx`, 'application/xml');
            }

            // Report completion
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'EXPORT_COMPLETE_MESSAGE'));
            }

        } catch (error) {
            console.error('Error exporting SESX project:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Convenience method: Export SESX file only (without media)
     *
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the SESX file
     */
    exportSESXFile: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: false });
        return this.exportSESXProject(sequencer, startNode, endNode, options);
    },

    /**
     * Convenience method: Export SESX with media files in zip package
     *
     * @param {Sequencer} sequencer - The sequencer instance
     * @param {SequencerNode} startNode - Start node (null for beginning)
     * @param {SequencerNode} endNode - End node (null for end)
     * @param {Object} options - Export options
     * @returns {Promise<void>} Downloads the zip package
     */
    exportSESXWithMedia: async function(sequencer, startNode, endNode, options) {
        options = _.extend({}, options, { includeMedia: true });
        return this.exportSESXProject(sequencer, startNode, endNode, options);
    },






    /**
     * Add media files from timeline data to a zip archive
     * 
     * @param {JSZip} zip - The zip instance to add files to
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {String} mediaFolder - Folder path within zip for media files
     * @param {Sequencer} sequencer - Sequencer instance for AudioBufferCache access
     * @returns {Promise<void>}
     */
    _addMediaFilesToZip: async function(zip, timelineData, mediaFolder, sequencer) {
        const addedFiles = new Set(); // Track added files to avoid duplicates
        const mediaPromises = [];
        
        // Count files for performance tracking
        let totalFiles = 0;
        for (const clip of timelineData) {
            if (clip.sourceMediaUrl && clip.fileName && !addedFiles.has(clip.fileName)) {
                totalFiles++;
            }
        }
        
        console.log(`SequencerExportHelper: Starting media file export (${totalFiles} unique files)`);
        
        for (const clip of timelineData) {
            if (clip.sourceMediaUrl && clip.fileName && !addedFiles.has(clip.fileName)) {
                addedFiles.add(clip.fileName);
                
                // Create promise to fetch and add media file
                const mediaPromise = this._fetchAndAddMediaFile(zip, clip.sourceMediaUrl, clip.fileName, mediaFolder, sequencer);
                mediaPromises.push(mediaPromise);
            }
        }
        
        // Wait for all media files to be added
        console.log('SequencerExportHelper: Waiting for all media files to be processed...');
        await Promise.all(mediaPromises);
        console.log('SequencerExportHelper: All media files processed successfully');
    },

    /**
     * Check if a media file is available in AudioBufferCache
     * 
     * @param {String} mediaUrl - URL of the media file
     * @param {Sequencer} sequencer - Sequencer instance for AudioBufferCache access
     * @returns {Promise<Blob|null>} Cached blob or null if not cached
     */
    // Export-specific blob cache to store blobs temporarily during export
    _exportBlobCache: new Map(),

    _getCachedBlobForUrl: async function(mediaUrl, sequencer) {
        if (!sequencer) {
            return null;
        }
        
        // First, check our export-specific cache to avoid re-fetching
        if (this._exportBlobCache.has(mediaUrl)) {
            console.log('SequencerExportHelper: Using export-cached blob for:', mediaUrl);
            return this._exportBlobCache.get(mediaUrl);
        }
        
        const audioBufferCache = sequencer.getAudioBufferCache();
        if (!audioBufferCache) {
            return null;
        }
        
        try {
            // Check if URL is in AudioBufferCache
            const cacheMap = audioBufferCache._urlToPromiseCachedDownloadedBlobUrl;
            if (cacheMap && cacheMap[mediaUrl]) {
                console.log('SequencerExportHelper: Found cached blob for:', mediaUrl);
                
                // Get the cached blob URL
                const cachedBlobUrl = await cacheMap[mediaUrl];
                if (cachedBlobUrl && cachedBlobUrl.startsWith('blob:')) {
                    try {
                        // Instead of creating a blob copy (which uses more memory),
                        // immediately fetch and use the data, then cache it in our export cache
                        const response = await fetch(cachedBlobUrl);
                        if (response.ok) {
                            const blob = await response.blob();
                            // Store in our export-specific cache to avoid re-fetching during the same export
                            this._exportBlobCache.set(mediaUrl, blob);
                            console.log('SequencerExportHelper: Successfully fetched and cached blob for export:', mediaUrl);
                            return blob;
                        } else {
                            console.warn('SequencerExportHelper: Failed to fetch from cached blob URL (response not ok):', cachedBlobUrl);
                        }
                    } catch (fetchError) {
                        console.warn('SequencerExportHelper: Blob URL may have been revoked, will fallback to network:', fetchError.message);
                        // Don't return null here - let it fall through to cache miss logging
                    }
                }
            }
            
            console.log('SequencerExportHelper: No cached blob found for:', mediaUrl);
            return null;
        } catch (error) {
            console.warn('SequencerExportHelper: Error accessing cached blob:', error);
            return null;
        }
    },

    /**
     * Clear export-specific blob cache to free memory
     */
    _clearExportBlobCache: function() {
        const cacheSize = this._exportBlobCache.size;
        this._exportBlobCache.clear();
        if (cacheSize > 0) {
            console.log(`SequencerExportHelper: Cleared export blob cache (${cacheSize} items)`);
        }
    },

    /**
     * Fetch a media file and add it to the zip
     * 
     * @param {JSZip} zip - The zip instance
     * @param {String} mediaUrl - URL of the media file
     * @param {String} fileName - Name for the file in the zip
     * @param {String} mediaFolder - Folder path within zip
     * @param {Sequencer} sequencer - Sequencer instance for AudioBufferCache access
     * @returns {Promise<void>}
     */
    _fetchAndAddMediaFile: async function(zip, mediaUrl, fileName, mediaFolder, sequencer) {
        try {
            
            // For blob URLs, we can access the blob directly
            if (mediaUrl.startsWith('blob:')) {
                console.log('SequencerExportHelper: Processing blob URL:', fileName);
                const response = await fetch(mediaUrl);
                const blob = await response.blob();
                zip.file(mediaFolder + fileName, blob);
                return;
            } 
            
            // For HTTP URLs, check cache first, then fallback to network
            if (mediaUrl.startsWith('http')) {
                console.log('SequencerExportHelper: Processing HTTP URL:', fileName, '| URL:', mediaUrl);
                
                // STEP 1: Try to get from AudioBufferCache first
                const cachedBlob = await this._getCachedBlobForUrl(mediaUrl, sequencer);
                if (cachedBlob) {
                    console.log('SequencerExportHelper: Using cached blob for:', fileName);
                    zip.file(mediaFolder + fileName, cachedBlob);
                    return;
                }
                
                // STEP 2: Not in cache, fetch from network
                console.log('SequencerExportHelper: Cache miss, fetching from network:', fileName);
                try {
                    // Use AudioBufferCache for URL optimization if available
                    let optimizedUrl = mediaUrl;
                    if (sequencer) {
                        const audioBufferCache = sequencer.getAudioBufferCache();
                        if (audioBufferCache) {
                            optimizedUrl = audioBufferCache.getCloudFrontUrlIfApplicable(mediaUrl);
                        }
                    }
                    
                    console.log('SequencerExportHelper: Network fetch attempt:', {
                        fileName,
                        originalUrl: mediaUrl,
                        optimizedUrl: optimizedUrl
                    });
                    
                    const response = await fetch(optimizedUrl, { mode: 'cors' });
                    if (response.ok) {
                        const blob = await response.blob();
                        zip.file(mediaFolder + fileName, blob);
                        console.log('SequencerExportHelper: Successfully fetched from network:', fileName);
                    } else {
                        console.warn(`Could not fetch media file: ${optimizedUrl} (${response.status})`);
                        // Add a placeholder text file indicating the missing media
                        zip.file(mediaFolder + fileName + '.missing.txt', 
                            `Original media file could not be included: ${mediaUrl}\nStatus: ${response.status}`);
                    }
                } catch (fetchError) {
                    console.warn(`Error fetching media file: ${mediaUrl}`, fetchError);
                    // Add a placeholder text file indicating the missing media
                    zip.file(mediaFolder + fileName + '.missing.txt', 
                        `Original media file could not be included: ${mediaUrl}\nError: ${fetchError.message}`);
                }
            }
            // For data URLs, convert to blob
            else if (mediaUrl.startsWith('data:')) {
                const response = await fetch(mediaUrl);
                const blob = await response.blob();
                zip.file(mediaFolder + fileName, blob);
            }
            // For other URLs, create a reference file
            else {
                zip.file(mediaFolder + fileName + '.url.txt', 
                    `Original media file location: ${mediaUrl}\nThis file could not be automatically included in the package.`);
            }
        } catch (error) {
            console.warn(`Error adding media file ${fileName}:`, error);
            // Add a placeholder text file indicating the error
            zip.file(mediaFolder + fileName + '.error.txt', 
                `Error including media file: ${error.message}\nOriginal URL: ${mediaUrl}`);
        }
    },

    /**
     * Download generated content as file
     */
    downloadFile: function(content, filename, mimeType) {
        try {
            let blob;
            
            // Handle different content types
            if (content instanceof Blob) {
                blob = content;
            } else {
                blob = new Blob([content], { type: mimeType || 'text/plain' });
            }
            
            const url = URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the URL object
            setTimeout(() => URL.revokeObjectURL(url), 100);
            
        } catch (error) {
            console.error('Error downloading file:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export transcript as text file (.txt) - Project ID approach
     * Loads project as needed from project ID
     * 
     * @param {String} projectId - The sequencer project ID
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.includeSpeakers - Include speaker identification
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<string>} The transcript text
     */
    exportTranscriptTxtFromProject: async function(projectId, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            includeSpeakers: true
        };
        options = _.extend(defaultOptions, options);

        const progressReportFunction = (percentageProgress, progressMessage) => {
            if (progressCallback) {
                progressCallback(percentageProgress, progressMessage);
            }
        };

        const textTranscript = await SequencerRendererUtils.getInstance().getTranscriptionTextFile(
            projectId, 
            options.includeSpeakers, 
            progressReportFunction
        );
        
        this.downloadFile(textTranscript, `${projectTitle}.txt`, 'text/plain;charset=utf-8');

        // Track the download
        try {
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(null, null, "textTxtTranscription", projectId);
        } catch (error) {
            console.error("Failed marking text transcription download. Error: " + error);
        }

        return textTranscript;
    },

    /**
     * Export transcript as text file (.txt) - Sequencer approach
     * Uses already loaded sequencer for efficiency
     * 
     * @param {Object} sequencer - The sequencer instance
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.includeSpeakers - Include speaker identification
     * @param {SequencerNode} options.startNode - Start node for selection (null for full project)
     * @param {SequencerNode} options.endNode - End node for selection (null for full project)
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<string>} The transcript text
     */
    exportTranscriptTxtFromSequencer: async function(sequencer, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            includeSpeakers: true,
            startNode: null,
            endNode: null
        };
        options = _.extend(defaultOptions, options);

        let textTranscript;
        
        if (options.startNode && options.endNode) {
            // Selection-based export
            textTranscript = sequencer.getSelectedNodesTextContent(
                options.startNode,
                options.endNode,
                options.includeSpeakers
            );
        } else {
            // Full project export using sequencer
            textTranscript = sequencer.getFullTextContent(options.includeSpeakers);
        }
        
        // For sequencer approach, we don't need the async rendering pipeline
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'EXPORT_COMPLETE_MESSAGE'));
        }
        
        this.downloadFile(textTranscript, `${projectTitle}.txt`, 'text/plain;charset=utf-8');

        // Track the download
        try {
            const projectId = sequencer.getProjectId ? sequencer.getProjectId() : null;
            const capsuleId = sequencer.getCapsuleId ? sequencer.getCapsuleId() : null;
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(capsuleId, null, "textTxtTranscription", projectId);
        } catch (error) {
            console.error("Failed marking text transcription download. Error: " + error);
        }

        return textTranscript;
    },

    /**
     * Export transcript as SRT or VTT subtitle file - Project ID approach
     * Loads project as needed from project ID
     * 
     * @param {String} projectId - The sequencer project ID
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.asVtt - Export as VTT instead of SRT
     * @param {Boolean} options.includeSpeakers - Include speaker identification
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<string>} The subtitle text
     */
    exportTranscriptSrtFromProject: async function(projectId, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            asVtt: false,
            includeSpeakers: true
        };
        options = _.extend(defaultOptions, options);

        const fileExtension = options.asVtt ? ".vtt" : ".srt";
        
        const progressReportFunction = (percentageProgress, progressMessage) => {
            if (progressCallback) {
                progressCallback(percentageProgress, progressMessage);
            }
        };

        const transcriptionText = await SequencerRendererUtils.getInstance().getSrtText(
            projectId, 
            options.asVtt, 
            true, 
            progressReportFunction
        );
        
        this.downloadFile(transcriptionText, `${projectTitle}${fileExtension}`, 'text/plain;charset=utf-8');

        // Track the download
        try {
            const trackingType = options.asVtt ? 'textVttTranscription' : 'textSrtTranscription';
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(null, null, trackingType, projectId);
        } catch (error) {
            console.error("Failed marking text transcription download. Error: " + error);
        }

        return transcriptionText;
    },

    /**
     * Export transcript as SRT or VTT subtitle file - Sequencer approach
     * Uses already loaded sequencer for efficiency
     * 
     * @param {Object} sequencer - The sequencer instance
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.asVtt - Export as VTT instead of SRT
     * @param {Boolean} options.includeSpeakers - Include speaker identification
     * @param {SequencerNode} options.startNode - Start node for selection (null for full project)
     * @param {SequencerNode} options.endNode - End node for selection (null for full project)
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<string>} The subtitle text
     */
    exportTranscriptSrtFromSequencer: async function(sequencer, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            asVtt: false,
            includeSpeakers: true,
            startNode: null,
            endNode: null
        };
        options = _.extend(defaultOptions, options);

        const fileExtension = options.asVtt ? ".vtt" : ".srt";
        
        const progressReportFunction = (percentageProgress, progressMessage) => {
            if (progressCallback) {
                progressCallback(percentageProgress, progressMessage);
            }
        };

        let transcriptionText;
        
        if (options.startNode && options.endNode) {
            // Selection-based export
            transcriptionText = await sequencer.getSrtText(
                options.startNode,
                options.endNode,
                progressReportFunction,
                options.asVtt,
                !options.includeSpeakers, // doNotIncludeSpeaker parameter
                null, // shouldSequencerNodeBePlayedValidatorFunction
                false // includeDeletedSegmentsWhenPossible
            );
        } else {
            // Full project export using sequencer
            transcriptionText = await sequencer.getSrtText(
                null, // startNode
                null, // endNode
                progressReportFunction,
                options.asVtt,
                !options.includeSpeakers, // doNotIncludeSpeaker parameter
                null, // shouldSequencerNodeBePlayedValidatorFunction
                false // includeDeletedSegmentsWhenPossible
            );
        }
        
        this.downloadFile(transcriptionText, `${projectTitle}${fileExtension}`, 'text/plain;charset=utf-8');

        // Track the download
        try {
            const projectId = sequencer.getProjectId ? sequencer.getProjectId() : null;
            const capsuleId = sequencer.getCapsuleId ? sequencer.getCapsuleId() : null;
            const trackingType = options.asVtt ? 'textVttTranscription' : 'textSrtTranscription';
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(capsuleId, null, trackingType, projectId);
        } catch (error) {
            console.error("Failed marking text transcription download. Error: " + error);
        }

        return transcriptionText;
    },

    /**
     * Export transcript as DOCX file - Project ID approach
     * Loads project as needed from project ID
     * 
     * @param {String} projectId - The sequencer project ID
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.includeSpeakers - Include speaker identification
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<Blob>} The DOCX blob
     */
    exportTranscriptDocxFromProject: async function(projectId, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            includeSpeakers: true
        };
        options = _.extend(defaultOptions, options);

        const progressReportFunction = (percentageProgress, progressMessage) => {
            if (progressCallback) {
                progressCallback(percentageProgress, progressMessage);
            }
        };

        const textTranscript = await SequencerRendererUtils.getInstance().getTranscriptionTextFile(
            projectId, 
            options.includeSpeakers, 
            progressReportFunction
        );
        
        // Generate DOCX document
        const docxBlob = await this._generateDocxFromText(textTranscript, projectTitle, options);
        
        // Download the file
        saveAs(docxBlob, `${projectTitle}.docx`);

        // Track the download
        try {
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(null, null, "textDocxTranscription", projectId);
        } catch (error) {
            console.error("Failed marking DOCX transcription download. Error: " + error);
        }

        return docxBlob;
    },

    /**
     * Export transcript as DOCX file - Sequencer approach
     * Uses already loaded sequencer for efficiency
     * 
     * @param {Object} sequencer - The sequencer instance
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.includeSpeakers - Include speaker identification
     * @param {SequencerNode} options.startNode - Start node for selection (null for full project)
     * @param {SequencerNode} options.endNode - End node for selection (null for full project)
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<Blob>} The DOCX blob
     */
    exportTranscriptDocxFromSequencer: async function(sequencer, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            includeSpeakers: true,
            startNode: null,
            endNode: null
        };
        options = _.extend(defaultOptions, options);

        let textTranscript;
        
        if (options.startNode && options.endNode) {
            // Selection-based export
            textTranscript = sequencer.getSelectedNodesTextContent(
                options.startNode,
                options.endNode,
                options.includeSpeakers
            );
        } else {
            // Full project export using sequencer
            textTranscript = sequencer.getFullTextContent(options.includeSpeakers);
        }
        
        // For sequencer approach, we don't need the async rendering pipeline
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'GENERATING_DOCX_CONTENT'));
        }
        
        // Generate DOCX document
        const docxBlob = await this._generateDocxFromText(textTranscript, projectTitle, options);
        
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'EXPORT_COMPLETE_MESSAGE'));
        }
        
        // Download the file
        saveAs(docxBlob, `${projectTitle}.docx`);

        // Track the download
        try {
            const projectId = sequencer.getProjectId ? sequencer.getProjectId() : null;
            const capsuleId = sequencer.getCapsuleId ? sequencer.getCapsuleId() : null;
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(capsuleId, null, "textDocxTranscription", projectId);
        } catch (error) {
            console.error("Failed marking DOCX transcription download. Error: " + error);
        }

        return docxBlob;
    },

    /**
     * Generate DOCX document from transcript text
     * @private
     * @param {String} textTranscript - The transcript text content
     * @param {String} projectTitle - Title for the document
     * @param {Object} options - Export options
     * @returns {Promise<Blob>} The DOCX blob
     */
    _generateDocxFromText: async function(textTranscript, projectTitle, options) {
        try {
            // Split transcript into paragraphs based on line breaks
            const lines = textTranscript.split('\n').filter(line => line.trim().length > 0);
            
            // Create paragraphs for the document
            const paragraphs = [];
            
            // Add title
            paragraphs.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: projectTitle || "Transcript",
                            bold: true,
                            size: 32, // 16pt font
                        }),
                    ],
                    spacing: {
                        after: 400, // 20pt spacing after
                    },
                })
            );
            
            // Add transcript content
            lines.forEach(line => {
                const trimmedLine = line.trim();
                if (trimmedLine) {
                    // Check if line contains speaker information (e.g., "Speaker 1:" or similar)
                    const speakerMatch = trimmedLine.match(/^([^:]+):\s*(.*)$/);
                    
                    if (speakerMatch && options.includeSpeakers) {
                        // Line has speaker info, format it with bold speaker name
                        const [, speaker, content] = speakerMatch;
                        paragraphs.push(
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: speaker + ": ",
                                        bold: true,
                                        color: "2563EB", // Blue color for speaker names
                                    }),
                                    new TextRun({
                                        text: content,
                                    }),
                                ],
                                spacing: {
                                    after: 200, // 10pt spacing after each paragraph
                                },
                            })
                        );
                    } else {
                        // Regular paragraph without speaker formatting
                        paragraphs.push(
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: trimmedLine,
                                    }),
                                ],
                                spacing: {
                                    after: 200, // 10pt spacing after each paragraph
                                },
                            })
                        );
                    }
                }
            });
            
            // Create the document
            const doc = new Document({
                sections: [
                    {
                        properties: {},
                        children: paragraphs,
                    },
                ],
                creator: "Trebble",
                title: projectTitle || "Transcript",
                description: "Transcript generated by Trebble",
            });
            
            // Generate blob
            const blob = await Packer.toBlob(doc);
            return blob;
            
        } catch (error) {
            console.error('Error generating DOCX:', error);
            throw new Error(window.getI18n(ti18n, 'EXPORT_FAILED') + ': ' + error.message);
        }
    },

    /**
     * Export audio file (MP3 or WAV) - Project ID approach
     * Loads project as needed from project ID
     * 
     * @param {String} projectId - The sequencer project ID
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.asWav - Export as WAV instead of MP3
     * @param {String} options.quality - Quality setting ('high', 'medium', 'low')
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<Blob>} The audio blob
     */
    exportAudioFromProject: async function(projectId, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            asWav: false,
            quality: 'high',
            skipAudioProcessing: false
        };
        options = _.extend(defaultOptions, options);

        const fileExtension = options.asWav ? "wav" : "mp3";
        const filename = `${projectTitle}.${fileExtension}`;

        const progressReportFunction = (percentageProgress, progressMessage) => {
            if (progressCallback) {
                progressCallback(percentageProgress, progressMessage);
            }
        };

        const audioBlob = await SequencerRendererUtils.getInstance().getRenderedAudioBlob(
            projectId,
            options.asWav,
            false,
            progressReportFunction,
            null,
            options.skipAudioProcessing
        );
        
        Utils.getInstance().downloadFile(audioBlob, filename);

        // Track the download
        try {
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(null, null, "audio", projectId);
        } catch (error) {
            console.error("Failed marking audio download. Error: " + error);
        }

        return audioBlob;
    },

    /**
     * Export audio file (MP3 or WAV) - Sequencer approach
     * Uses already loaded sequencer for efficiency
     * 
     * @param {Object} sequencer - The sequencer instance
     * @param {String} projectTitle - Title for the file name
     * @param {Object} options - Export options
     * @param {Boolean} options.asWav - Export as WAV instead of MP3
     * @param {String} options.quality - Quality setting ('high', 'medium', 'low')
     * @param {SequencerNode} options.startNode - Start node for selection (null for full project)
     * @param {SequencerNode} options.endNode - End node for selection (null for full project)
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @returns {Promise<Blob>} The audio blob
     */
    exportAudioFromSequencer: async function(sequencer, projectTitle, options = {}, progressCallback = null) {
        const defaultOptions = {
            asWav: false,
            quality: 'high',
            startNode: null,
            endNode: null,
            skipAudioProcessing: false,
            applyBroadcastLoudnessNormalization: true // FFmpeg loudnorm (EBU R128 with true peak)
        };
        options = _.extend(defaultOptions, options);

        // When skipAudioProcessing is true, disable all post-render normalization
        if (options.skipAudioProcessing) {
            options.applyBroadcastLoudnessNormalization = false;
        }

        const fileExtension = options.asWav ? "wav" : "mp3";
        const filename = `${projectTitle}.${fileExtension}`;

        const progressReportFunction = (percentageProgress, progressMessage) => {
            if (progressCallback) {
                progressCallback(percentageProgress, progressMessage);
            }
        };

        let audioBlob;

        if (options.startNode && options.endNode) {
            // Selection-based export - use the existing method from SequencerController
            audioBlob = await sequencer.getRenderedAudioBlobClip(
                options.asWav, // renderWavFile
                options.startNode, // sequencerNodeToStartFrom
                options.endNode, // sequencerNodeToEndOn
                false, // asVideo
                progressReportFunction,
                null, // alreadyRenderedAudioWAVBlob
                null, // shouldSequencerNodeBePlayedValidatorFunction
                false, // includeDeletedSegmentsWhenPossible
                options.skipAudioProcessing // skipAudioProcessing
            );
        } else {
            // Full project export using sequencer
            audioBlob = await sequencer.getRenderedAudioBlobClip(
                options.asWav, // renderWavFile
                null, // sequencerNodeToStartFrom (null for full project)
                null, // sequencerNodeToEndOn (null for full project)
                false, // asVideo
                progressReportFunction,
                null, // alreadyRenderedAudioWAVBlob
                null, // shouldSequencerNodeBePlayedValidatorFunction
                false, // includeDeletedSegmentsWhenPossible
                options.skipAudioProcessing // skipAudioProcessing
            );
        }

        // Apply broadcast-compliant loudness normalization if requested
        if (options.applyBroadcastLoudnessNormalization) {
            try {
                if (progressCallback) {
                    progressCallback(0.9, window.getI18n(sequencerI18n, 'NORMALIZING_VOLUME_AUDIO'));
                }

                const audioBufferCache = sequencer.getAudioBufferCache();
                const outputFormat = options.asWav ? 'wav' : 'mp3';

                // Use memory-efficient Blob-based normalization
                audioBlob = await audioBufferCache.applyFFmpegLoudnormToBlob(
                    audioBlob,
                    {
                        targetLoudness: -16,  // EBU R128 broadcast standard
                        truePeak: -1.5,       // Safe true peak ceiling
                        loudnessRange: 11,    // EBU R128 recommended
                        outputFormat: outputFormat
                    },
                    (ffmpegProgress) => {
                        // Map FFmpeg progress (0-1) to final 10% of overall progress (0.9-1.0)
                        if (progressCallback) {
                            const overallProgress = 0.9 + (ffmpegProgress * 0.1);
                            progressCallback(overallProgress, window.getI18n(sequencerI18n, 'NORMALIZING_VOLUME_AUDIO'));
                        }
                    }
                );

                if (progressCallback) {
                    progressCallback(1.0, window.getI18n(ti18n, 'EXPORT_COMPLETE_MESSAGE'));
                }
            } catch (error) {
                console.error("FFmpeg loudnorm failed, using original audio:", error);
                // Continue with original audio blob (graceful degradation)
            }
        }

        Utils.getInstance().downloadFile(audioBlob, filename);

        // Track the download
        try {
            const projectId = sequencer.getProjectId ? sequencer.getProjectId() : null;
            const capsuleId = sequencer.getCapsuleId ? sequencer.getCapsuleId() : null;
            TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(capsuleId, null, "audio", projectId);
        } catch (error) {
            console.error("Failed marking audio download. Error: " + error);
        }

        return audioBlob;
    },

    /**
     * Export video file 
     * Supports both projectId-based and sequencer-based approaches
     * Either downloads existing video URL or triggers cloud video rendering
     * 
     * @param {String|Object} projectIdOrSequencer - Project ID string OR sequencer instance
     * @param {String} projectTitle - Title for the file name
     * @param {String} videoUrl - URL of the video to download (null to trigger rendering)
     * @param {Object} options - Export options
     * @param {SequencerNode} options.startNode - Start node for selection (sequencer approach)
     * @param {SequencerNode} options.endNode - End node for selection (sequencer approach)
     * @param {Function} progressCallback - Progress callback (percentage, message)
     * @param {Object} sequencerController - Optional sequencer controller instance
     * @returns {Promise<boolean>} Success indicator
     */
    exportVideo: async function(projectIdOrSequencer, projectTitle, videoUrl, options = {}, progressCallback = null, sequencerController = null) {
        const defaultOptions = {
            startNode: null,
            endNode: null
        };
        options = _.extend(defaultOptions, options);

        const downloadProgressFunction = (progressUnderOne, progressMessage) => {
            if (progressCallback) {
                progressCallback(progressUnderOne * 100, progressMessage);
            }
        };

        const { sequencer, projectId } = this._resolveSequencerAndProjectId(projectIdOrSequencer);

        // Debug logging to understand what's being passed
        console.log('SequencerExportHelper.exportVideo called with:', {
            hasSequencer: !!sequencer,
            hasController: !!sequencerController,
            hasVideoUrl: !!videoUrl,
            isSelection: !!(options.startNode && options.endNode)
        });

        try {
            // If we have a video URL, download it directly
            if (videoUrl && videoUrl.trim() !== '') {
                if (progressCallback) {
                    progressCallback(null, window.getI18n(ti18n, 'DOWNLOADING_VIDEO_FILE'));
                }
                
                const filename = `${projectTitle}.mp4`;
                await Utils.getInstance().downloadURI(videoUrl, filename, downloadProgressFunction);
                
                // Track the download (best-effort)
                try {
                    const capsuleId = sequencer && sequencer.getCapsuleId ? sequencer.getCapsuleId() : null;
                    const trackingProjectId = sequencer ? (sequencer.getProjectId ? sequencer.getProjectId() : null) : projectId;
                    TrebbleClientAPIHelper.getInstance().trackCapsuleDownload(capsuleId, videoUrl, "videoDownload", trackingProjectId);
                } catch (trackingError) {
                    console.warn('Failed to track video download:', trackingError);
                }
                
                return true;
            }

            // No existing video URL - need to trigger cloud video rendering
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'PREPARING_VIDEO_RENDERING'));
            }

            // Check if video rendering is available
            const Rollout = FeatureRolloutHelper.getInstance();
            const canRenderVideo = Rollout.isFeatureEnabled(Rollout.FEATURES.RENDER_VIDEO_IN_CLOUD, true);
            
            if (!canRenderVideo) {
                const error = new Error(window.getI18n(ti18n, 'VIDEO_RENDERING_NOT_AVAILABLE'));
                error.name = 'VIDEO_RENDERING_NOT_AVAILABLE';
                throw error;
            }

            // Execute video rendering with appropriate sequencer and controller setup
            await this._executeVideoRendering(sequencer, projectId, options, progressCallback, sequencerController);

            if (progressCallback) {
                progressCallback(100, window.getI18n(ti18n, 'VIDEO_RENDERING_STARTED'));
            }

            return true;

        } catch (error) {
            console.error('Video export error:', error);

            // Provide user-friendly error messages based on error type
            if (error.name === 'VIDEO_RENDERING_NOT_AVAILABLE') {
                throw new Error(window.getI18n(ti18n, 'VIDEO_FEATURE_NOT_ENABLED'));
            } else if (error.name === 'SEQUENCER_CONTROLLER_REQUIRED_FOR_VIDEO') {
                throw new Error(window.getI18n(ti18n, 'VIDEO_EXPORT_TEMPORARILY_UNAVAILABLE'));
            } else {
                const errorMessage = error && error.message ? error.message : String(error);
                throw new Error(window.getI18n(ti18n, 'VIDEO_EXPORT_FAILED') + ': ' + errorMessage);
            }
        }
    },

    /**
     * Resolve sequencer and project ID from mixed input parameter
     * @private
     * @param {Object|String} projectIdOrSequencer - Sequencer instance or project ID
     * @returns {Object} Object with sequencer and projectId properties
     */
    _resolveSequencerAndProjectId: function(projectIdOrSequencer) {
        let projectId;
        let sequencer = null;

        // Determine if we're working with project ID or sequencer instance
        if (
            projectIdOrSequencer &&
            typeof projectIdOrSequencer === 'object' &&
            (typeof projectIdOrSequencer.getProjectId === 'function' ||
             typeof projectIdOrSequencer.getCapsuleId === 'function')
        ) {
            sequencer = projectIdOrSequencer;
            projectId = sequencer.getProjectId ? sequencer.getProjectId() : sequencer.getCapsuleId();
        } else {
            projectId = String(projectIdOrSequencer);
        }

        return { sequencer, projectId };
    },

    /**
     * Export video using Timeloom + Remotion Lambda rendering
     * Modern cloud-based video rendering using Remotion for professional quality
     *
     * @param {Object|String} projectIdOrSequencer - Sequencer instance or project ID
     * @param {String} projectTitle - Title for the exported video file
     * @param {Object} options - Export options
     * @param {SequencerNode} options.startNode - Optional start node for selection export
     * @param {SequencerNode} options.endNode - Optional end node for selection export
     * @param {String} options.aspectRatio - Video aspect ratio ('16:9', '9:16', '1:1', '4:5')
     * @param {Number} options.frameRate - Target frame rate (23.976, 24, 25, 29.97, 30, 60)
     * @param {String} options.videoTierResolution - Resolution tier ('low', 'medium', 'high', 'ultra', '4k')
     * @param {Function} progressCallback - Progress callback function(percentage, message)
     * @param {Object} sequencerController - Optional sequencer controller instance
     * @returns {Promise<boolean>} Returns true on success
     */
    exportVideoWithTimeloom: async function(projectIdOrSequencer, projectTitle, options = {}, progressCallback = null, sequencerController = null) {
        const defaultOptions = {
            startNode: null,
            endNode: null,
            aspectRatio: '16:9',
            frameRate: 25,
            videoTierResolution: 'medium',
            backgroundColor: '#000000'
        };
        options = _.extend(defaultOptions, options);

        const { sequencer, projectId } = this._resolveSequencerAndProjectId(projectIdOrSequencer);

        console.log('SequencerExportHelper.exportVideoWithTimeloom called with:', {
            hasSequencer: !!sequencer,
            hasController: !!sequencerController,
            isSelection: !!(options.startNode && options.endNode),
            aspectRatio: options.aspectRatio,
            frameRate: options.frameRate,
            videoTierResolution: options.videoTierResolution
        });

        try {
            // Check if Timeloom/Remotion rendering is available
            const Rollout = FeatureRolloutHelper.getInstance();
            const canRenderWithTimeloom = Rollout.isFeatureEnabled(Rollout.FEATURES.RENDER_VIDEO_IN_CLOUD, true);

            if (!canRenderWithTimeloom) {
                const error = new Error(window.getI18n(ti18n, 'VIDEO_RENDERING_NOT_AVAILABLE'));
                error.name = 'VIDEO_RENDERING_NOT_AVAILABLE';
                throw error;
            }

            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'PREPARING_VIDEO_RENDERING'));
            }

            // Execute Timeloom rendering with appropriate sequencer and controller setup
            await this._executeTimeloomRendering(sequencer, projectId, projectTitle, options, progressCallback, sequencerController);

            if (progressCallback) {
                progressCallback(100, window.getI18n(ti18n, 'VIDEO_RENDERING_STARTED'));
            }

            return true;

        } catch (error) {
            console.error('Timeloom video export error:', error);

            // Provide user-friendly error messages based on error type
            if (error.name === 'VIDEO_RENDERING_NOT_AVAILABLE') {
                throw new Error(window.getI18n(ti18n, 'VIDEO_FEATURE_NOT_ENABLED'));
            } else if (error.name === 'SEQUENCER_CONTROLLER_REQUIRED_FOR_VIDEO' || error.name === 'SEQUENCER_REQUIRED') {
                throw new Error(window.getI18n(ti18n, 'SEQUENCER_CONTROLLER_REQUIRED_FOR_VIDEO'));
            } else {
                const errorMessage = error && error.message ? error.message : String(error);
                throw new Error(window.getI18n(ti18n, 'VIDEO_EXPORT_FAILED') + ': ' + errorMessage);
            }
        }
    },

    /**
     * Execute Timeloom video rendering with proper sequencer setup
     * Converts Sequencer timeline to Timeloom/Remotion format and triggers cloud rendering
     * @private
     *
     * @param {Object} sequencer - Sequencer instance (null if needs to be loaded)
     * @param {String} projectId - Project ID for loading sequencer if needed
     * @param {String} projectTitle - Title for the video
     * @param {Object} options - Export options including startNode, endNode, aspectRatio, frameRate
     * @param {Function} progressCallback - Progress callback
     * @param {Object} sequencerController - Optional sequencer controller instance
     * @returns {Promise<void>}
     */
    _executeTimeloomRendering: async function(sequencer, projectId, projectTitle, options, progressCallback, sequencerController) {
        // If sequencer is provided, use it directly
        if (sequencer) {
            await this._performTimeloomRendering(sequencer, projectId, projectTitle, options, progressCallback);
            return;
        }

        // Need to load sequencer
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'LOADING_PROJECT_FOR_RENDERING'));
        }

        // Use withSequencer to properly load and manage the sequencer
        await SequencerRendererUtils.getInstance().withSequencer(projectId, async (loadedSequencer) => {
            await this._performTimeloomRendering(loadedSequencer, projectId, projectTitle, options, progressCallback);
        }, progressCallback);
    },

    /**
     * Perform Timeloom video rendering using the sequencer
     * Converts timeline to Remotion input format and starts cloud rendering
     * @private
     *
     * @param {Object} sequencer - Sequencer instance
     * @param {String} projectId - Project ID
     * @param {String} projectTitle - Title for the video
     * @param {Object} options - Export options
     * @param {Function} progressCallback - Progress callback
     * @returns {Promise<void>}
     */
    _performTimeloomRendering: async function(sequencer, projectId, projectTitle, options, progressCallback) {
        // Import TimeloomRenderingInputBuilder dynamically with error handling
        let TimeloomRenderingInputBuilder;
        try {
            TimeloomRenderingInputBuilder = (await import(/* webpackChunkName: "timeloomRenderingInputBuilder" */ './../../components/audioEditor/TimeloomRenderingInputBuilder')).default;
        } catch (importError) {
            // Log detailed context about the failed import
            console.error('[SequencerExportHelper] Failed to import TimeloomRenderingInputBuilder:', {
                error: importError.message,
                stack: importError.stack,
                projectId,
                projectTitle,
                options
            });

            // Rethrow with additional context for upstream error handling
            const contextError = new Error(`Failed to load Timeloom rendering module: ${importError.message}`);
            contextError.originalError = importError;
            contextError.context = { projectId, projectTitle, options };
            throw contextError;
        }

        // Get merged nodes from sequencer (same approach as FFmpeg rendering)
        const startNode = options.startNode || sequencer.getFirstNode();
        const endNode = options.endNode || sequencer.getLastNode();

        const mergedNodes = await sequencer.getPreparedVideoDataForClipCreation(startNode, endNode, null, false);

        // Render and upload audio FIRST (following FFmpeg pattern from SequencerController.js:796-803)
        let audioFileUrl = null;

        // Render audio to WAV blob (same as FFmpeg approach)
        // Let internal rendering show detailed progress (RENDERING_AUDIO_UNCOMPRESSED, NORMALIZING)
        // Only transform percentage from 0-1 to 0-100
        const audioProgressFunction = (p, message) => {
            if (progressCallback) {
                // If we have a message from internal rendering, use it
                // Otherwise default to generic "RENDERING_AUDIO"
                const displayMessage = message || window.getI18n(ti18n, 'RENDERING_AUDIO');
                return progressCallback(p ? p * 100 : null, displayMessage);
            }
        };
        const audioRenderedBlob = await sequencer.getRenderedAudioBlobClip(
            true,  // renderWavFile - render as WAV format
            startNode,  // sequencerNodeToStartFrom - use computed selection start
            endNode,  // sequencerNodeToEndOn - use computed selection end
            false, // asVideo - not rendering as video
            audioProgressFunction, // progressReportFunction
            null,  // alreadyRenderedAudioWAVBlob - no pre-rendered blob
            null,  // shouldSequencerNodeBePlayedValidatorFunction - no custom validator
            false  // includeDeletedSegmentsWhenPossible - don't include deleted segments
        );

        // Upload audio to S3 with presigned URL (following FFmpeg pattern from SequencerController.js:800-803)
        const {presignedUrl: presignedUrlForAudio, fileLocationUrl: fileLocationUrlForAudio} =
            await TrebbleClientAPIHelper.getInstance().getPresignedUrlForUploadingTemporaryAudioOrVideoFile("audio/wav");

        // Upload progress callback - transforms 0-1 progress to 0-100% and shows "PREPARING_AUDIO" message
        const uploadProgressFunction = (p) => {
            if (progressCallback) {
                return progressCallback(p * 100, window.getI18n(ti18n, 'PREPARING_AUDIO'));
            }
        };

        await FileUploadHelper.getInstance().uploadBlobToS3UsignPreSignedUrlWithProgress(
            audioRenderedBlob,
            presignedUrlForAudio,
            uploadProgressFunction
        );
        audioFileUrl = fileLocationUrlForAudio;

        console.log('[TimeloomRendering] Audio rendered and uploaded:', {
            audioFileUrl,
            audioBlobSize: audioRenderedBlob.size
        });

        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'PREPARING_VIDEO_TIMELINE'));
        }

        // Detect environment from window.location.hostname
        const getClientEnvironment = () => {
            const hostname = window.location.hostname;
            if (hostname === 'web.trebble.fm') return 'production';
            if (hostname === 'beta-web.trebble.fm') return 'beta';
            return 'development';
        };

        // Convert merged nodes to Timeloom/Remotion input format WITH audio URL and environment
        const builder = new TimeloomRenderingInputBuilder();
        const remotionInputData = builder.buildFromMergedNodes(mergedNodes, {
            title: projectTitle,
            aspectRatio: options.aspectRatio,
            backgroundColor: options.backgroundColor,
            frameRate: options.frameRate,
            audioFileUrl: audioFileUrl,  // ✅ Pass audio URL to builder
            environment: getClientEnvironment()  // ✅ Pass explicit environment
        });

        // No transformation needed - TimeloomRenderingInputBuilder now outputs schema-compliant format
        // remotionInputData has: { timeline: { settings, scenes, ... }, aspectRatio, fps, durationInMs }

        // Debug: Log the remotion input (already in correct schema format from builder)
        console.log('[SequencerExportHelper] remotionInputData:', JSON.stringify(remotionInputData, null, 2));
        console.log('[SequencerExportHelper] timeline.scenes length:', remotionInputData.timeline?.scenes?.length || 0);
        console.log('[SequencerExportHelper] audioFileUrl passed to builder:', audioFileUrl);

        // Show progress for uploading video data
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'UPLOADING_VIDEO_DATA'));
        }

        // Get presigned URL for uploading rendering input data
        const { presignedUrl, fileLocationUrl } = await TrebbleClientAPIHelper.getInstance()
            .getPresignedUrlForUploadingTimeloomRenderingInputData();

        // Upload the Remotion input data to S3
        await FileUploadHelper.getInstance().uploadJSONToS3UsignPreSignedUrl(
            remotionInputData,
            presignedUrl
        );

        const srtFileUrl = null;
        const capsuleId = sequencer.getCapsuleId ? sequencer.getCapsuleId() : null;
        const workspaceId = sequencer.getWorkspaceId ? sequencer.getWorkspaceId() : undefined;

        // Determine if this is a selection export (temporary) or full project (permanent)
        const useTemporaryLocation = !!(options.startNode && options.endNode);

        // Start the Timeloom video rendering in the cloud with audio URL
        await TrebbleClientAPIHelper.getInstance().startTimeloomVideoRenderingInCloud(
            projectId,
            capsuleId,
            audioFileUrl,  // ✅ Now includes main audio track
            srtFileUrl,
            useTemporaryLocation, // ✅ true for selections, false for full project
            remotionInputData,
            fileLocationUrl,
            workspaceId
        );

        if (progressCallback) {
            progressCallback(100, window.getI18n(ti18n, 'RENDERING_IN_PROGRESS'));
        }

        console.log('Timeloom video rendering started successfully', {
            projectId,
            capsuleId,
            remotionInputDataUrl: fileLocationUrl,
            aspectRatio: options.aspectRatio,
            frameRate: options.frameRate
        });
    },

    /**
     * Execute video rendering with proper sequencer and controller setup
     * Always uses SequencerRendererUtils.withSequencer when loading is needed
     * @private
     *
     * @param {Object} sequencer - Sequencer instance (null if needs to be loaded)
     * @param {String} projectId - Project ID for loading sequencer if needed
     * @param {Object} options - Export options including startNode, endNode
     * @param {Function} progressCallback - Progress callback
     * @param {Object} sequencerController - Optional sequencer controller instance
     * @returns {Promise<void>}
     */
    _executeVideoRendering: async function(sequencer, projectId, options, progressCallback, sequencerController) {
        // If both sequencer and controller are available, use them directly
        if (sequencer && sequencerController) {
            if (progressCallback) {
                progressCallback(null, window.getI18n(ti18n, 'STARTING_VIDEO_RENDERING'));
            }
            await this._triggerVideoRenderingWithController(
                sequencer, 
                sequencerController, 
                options, 
                progressCallback
            );
            return;
        }

        // If we have a sequencer but no controller, we can't proceed
        if (sequencer && !sequencerController) {
            const error = new Error(window.getI18n(ti18n, 'SEQUENCER_CONTROLLER_REQUIRED_FOR_VIDEO'));
            error.name = 'SEQUENCER_CONTROLLER_REQUIRED_FOR_VIDEO';
            throw error;
        }

        // Neither sequencer nor controller available - need to load sequencer
        if (progressCallback) {
            progressCallback(null, window.getI18n(ti18n, 'LOADING_PROJECT_FOR_RENDERING'));
        }

        // Use withSequencer to properly load and manage the sequencer
        await SequencerRendererUtils.getInstance().withSequencer(projectId, async (loadedSequencer) => {
            // Controller must be provided since we can't create one
            if (!sequencerController) {
                const error = new Error(window.getI18n(ti18n, 'SEQUENCER_CONTROLLER_REQUIRED_FOR_VIDEO'));
                error.name = 'SEQUENCER_CONTROLLER_REQUIRED_FOR_VIDEO';
                throw error;
            }

            await this._triggerVideoRenderingWithController(
                loadedSequencer, 
                sequencerController, 
                options, 
                progressCallback,
                false // No need to restore since we're using withSequencer
            );
        }, progressCallback);
    },

    /**
     * Trigger video rendering using sequencer controller
     * Simple delegation to the existing tested controller method
     * @private
     * 
     * @param {Object} sequencer - Sequencer instance
     * @param {Object} sequencerController - SequencerController instance
     * @param {Object} options - Export options with startNode, endNode
     * @param {Function} progressCallback - Progress callback
     * @param {Boolean} needsControllerRestore - Whether to restore original controller state
     * @returns {Promise<void>}
     */
    _triggerVideoRenderingWithController: async function(sequencer, sequencerController, options, progressCallback, needsControllerRestore = false) {
        let originalSequencer = null;

        try {
            // Set the sequencer on the controller if needed
            if (needsControllerRestore) {
                originalSequencer = sequencerController.getModel();
                sequencerController.setModel(sequencer);
            }

            // Hide external progress immediately - video exports use internal progress only (matches downloadSelectionAsClip)
            if (progressCallback) {
                progressCallback('HIDE_EXTERNAL_PROGRESS', window.getI18n(ti18n, 'STARTING_VIDEO_RENDERING'));
            }
            
            // Simple delegation to existing working method - exactly like downloadSelectionAsClip does
            const useTemporaryLocation = !!(options.startNode && options.endNode); // true for selections
            const showProgressDialog = true; // Enable internal progress dialog - matches downloadSelectionAsClip
            
            // Delegate to existing working method - just like downloadSelectionAsClip
            await sequencerController.startVideoRenderingInCloudForLaterDownload(
                options.startNode || null, // sequencerNodeToStartFrom
                options.endNode || null,   // sequencerNodeToEndOn
                null,                      // audioUrlInCloud - let the method handle audio rendering
                null,                      // unknown parameter (matches downloadSelectionAsClip)
                useTemporaryLocation,      // useTemporaryLocation
                showProgressDialog         // showProgressDialog - TRUE to enable internal progress
            );
            
            // No completion callback needed - internal method handles success messaging

        } catch (error) {
            console.error('Error in video rendering:', error);
            throw error;
        } finally {
            // Restore original sequencer if needed
            if (needsControllerRestore && originalSequencer) {
                sequencerController.setModel(originalSequencer);
            }
        }
    },

});

// Create singleton instance
const SequencerExportHelperInstance = new SequencerExportHelper();

// Export singleton instance
export default {
    getInstance: function() {
        return SequencerExportHelperInstance;
    }
};
